// src/utils/analytics.js - Sistema completo de Google Analytics com react-ga4
import ReactGA from 'react-ga4';

// Measurement ID do Google Analytics
const GA_MEASUREMENT_ID = 'G-HT9SQ5Z3TN';

// Flag para verificar se o GA foi inicializado
let isInitialized = false;

/**
 * Inicializa o Google Analytics
 * Deve ser chamado uma vez no início da aplicação
 */
export const initGA = () => {
  if (isInitialized) {
    return;
  }

  try {
    ReactGA.initialize(GA_MEASUREMENT_ID, {
      gaOptions: {
        send_page_view: false, // Desabilita pageview automático
      },
      gtagOptions: {
        anonymize_ip: true, // Anonimiza IPs para LGPD/GDPR
      },
    });
    isInitialized = true;
  } catch (error) {
    // Erro silencioso
  }
};

/**
 * Rastreia visualização de página
 * @param {string} path - Caminho da página (ex: '/about')
 * @param {string} title - Título da página
 */
export const trackPageView = (path, title) => {
  if (!isInitialized) {
    initGA();
  }

  try {
    ReactGA.send({
      hitType: 'pageview',
      page: path || window.location.pathname,
      title: title || document.title,
    });
  } catch (error) {
    // Erro silencioso
  }
};

/**
 * Rastreia evento customizado
 * @param {string} category - Categoria do evento (ex: 'User', 'Navigation')
 * @param {string} action - Ação do evento (ex: 'click', 'scroll')
 * @param {string} label - Label do evento (opcional)
 * @param {number} value - Valor numérico (opcional)
 */
export const trackEvent = (category, action, label = '', value = undefined) => {
  if (!isInitialized) {
    return;
  }

  try {
    ReactGA.event({
      category,
      action,
      label,
      value,
    });
  } catch (error) {
    // Erro silencioso
  }
};

/**
 * Rastreia clique em botão
 * @param {string} buttonName - Nome do botão
 * @param {string} location - Localização do botão (ex: 'header', 'footer')
 */
export const trackButtonClick = (buttonName, location = '') => {
  trackEvent('Button', 'click', `${buttonName}${location ? ` - ${location}` : ''}`, 1);
};

/**
 * Rastreia download de arquivo
 * @param {string} fileName - Nome do arquivo
 * @param {string} fileType - Tipo do arquivo (ex: 'pdf', 'docx')
 */
export const trackFileDownload = (fileName, fileType = '') => {
  trackEvent('Download', 'file', `${fileName}${fileType ? ` (${fileType})` : ''}`, 1);
};

/**
 * Rastreia troca de idioma
 * @param {string} fromLang - Idioma anterior
 * @param {string} toLang - Novo idioma
 */
export const trackLanguageChange = (fromLang, toLang) => {
  trackEvent('Language', 'change', `${fromLang} → ${toLang}`, 1);
};

/**
 * Rastreia interação com chat
 * @param {string} action - Ação (ex: 'message_sent', 'suggestion_clicked')
 * @param {string} details - Detalhes adicionais
 */
export const trackChatInteraction = (action, details = '') => {
  trackEvent('Chat', action, details, 1);
};

/**
 * Rastreia scroll na página
 * @param {number} percentage - Porcentagem de scroll (25, 50, 75, 100)
 */
export const trackScroll = (percentage) => {
  trackEvent('Engagement', 'scroll', `${percentage}%`, percentage);
};

/**
 * Rastreia tempo na página
 * @param {number} seconds - Tempo em segundos
 */
export const trackTimeOnPage = (seconds) => {
  trackEvent('Engagement', 'time_on_page', `${seconds}s`, seconds);
};

/**
 * Rastreia erro
 * @param {string} errorMessage - Mensagem de erro
 * @param {string} errorLocation - Onde o erro ocorreu
 */
export const trackError = (errorMessage, errorLocation = '') => {
  trackEvent('Error', 'exception', `${errorLocation}: ${errorMessage}`, 0);
};

/**
 * Rastreia clique em link externo
 * @param {string} url - URL do link
 * @param {string} linkText - Texto do link
 */
export const trackExternalLink = (url, linkText = '') => {
  trackEvent('External Link', 'click', `${linkText || url}`, 1);
};

/**
 * Rastreia visualização de seção
 * @param {string} sectionName - Nome da seção
 */
export const trackSectionView = (sectionName) => {
  trackEvent('Section', 'view', sectionName, 1);
};

/**
 * Rastreia interação com vídeo
 * @param {string} action - Ação (ex: 'play', 'pause', 'complete')
 * @param {string} videoTitle - Título do vídeo
 */
export const trackVideoInteraction = (action, videoTitle = '') => {
  trackEvent('Video', action, videoTitle, 1);
};

/**
 * Rastreia busca/pesquisa
 * @param {string} searchTerm - Termo pesquisado
 */
export const trackSearch = (searchTerm) => {
  trackEvent('Search', 'query', searchTerm, 1);
};

/**
 * Rastreia interação com formulário
 * @param {string} action - Ação (ex: 'submit', 'error', 'start')
 * @param {string} formName - Nome do formulário
 */
export const trackFormInteraction = (action, formName = '') => {
  trackEvent('Form', action, formName, 1);
};

/**
 * Rastreia compartilhamento social
 * @param {string} platform - Plataforma (ex: 'facebook', 'twitter', 'linkedin')
 * @param {string} contentType - Tipo de conteúdo compartilhado
 */
export const trackSocialShare = (platform, contentType = '') => {
  trackEvent('Social', 'share', `${platform}${contentType ? ` - ${contentType}` : ''}`, 1);
};

// Exporta também as funções legadas para compatibilidade
export const pageview = trackPageView;
export const event = trackEvent;

// Exporta o Measurement ID para uso em outros lugares se necessário
export { GA_MEASUREMENT_ID };
